import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor, Share } from 'lucide-react';

function getDeviceInfo() {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isMac = /Macintosh/.test(ua) && !('ontouchend' in document);
    const isAndroid = /Android/.test(ua);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
    return { isIOS, isMac, isAndroid, isStandalone };
}

export function InstallBanner() {
    const [show, setShow] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [device, setDevice] = useState({});

    useEffect(() => {
        const info = getDeviceInfo();
        setDevice(info);
        if (info.isStandalone) return;

        const dismissed = localStorage.getItem('install-dismissed');
        if (dismissed) return;

        // Android/desktop Chrome: capture beforeinstallprompt
        const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setShow(true); };
        window.addEventListener('beforeinstallprompt', handler);

        // Always show install instructions after short delay
        const timer = setTimeout(() => {
            if (!deferredPrompt) setShow(true);
        }, 1500);

        return () => { window.removeEventListener('beforeinstallprompt', handler); clearTimeout(timer); };
    }, [deferredPrompt]);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            setDeferredPrompt(null);
        }
        setShow(false);
    };

    const handleDismiss = () => {
        localStorage.setItem('install-dismissed', '1');
        setShow(false);
    };

    if (!show) return null;

    return (
        <div className="bg-zinc-950 text-white p-4 rounded-xl border-2 border-[#A78BFA] mb-6 relative" data-testid="install-banner">
            <button onClick={handleDismiss} className="absolute top-3 right-3 text-zinc-400 hover:text-white" data-testid="install-dismiss">
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#A78BFA] flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                    <h3 className="font-bold text-sm mb-1">Install as App</h3>
                    {deferredPrompt ? (
                        <>
                            <p className="text-zinc-400 text-xs mb-3">Get the full app experience — works offline, launches instantly.</p>
                            <button onClick={handleInstall} className="bg-[#A78BFA] text-white font-bold text-xs px-4 py-2 rounded-lg border-2 border-zinc-950" data-testid="install-button">
                                Install Now
                            </button>
                        </>
                    ) : device.isIOS ? (
                        <div className="text-zinc-400 text-xs space-y-1">
                            <p className="flex items-center gap-1.5"><Share className="w-3.5 h-3.5 inline flex-shrink-0" /> Tap <strong className="text-white">Share</strong> in Safari</p>
                            <p className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 inline flex-shrink-0" /> Then tap <strong className="text-white">"Add to Home Screen"</strong></p>
                        </div>
                    ) : device.isAndroid ? (
                        <div className="text-zinc-400 text-xs space-y-1">
                            <p className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 inline flex-shrink-0" /> Tap the <strong className="text-white">menu (three dots)</strong> in Chrome</p>
                            <p className="flex items-center gap-1.5"><Download className="w-3.5 h-3.5 inline flex-shrink-0" /> Then tap <strong className="text-white">"Install app"</strong></p>
                        </div>
                    ) : (
                        <div className="text-zinc-400 text-xs space-y-1">
                            <p className="flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5 inline flex-shrink-0" /> In Chrome/Edge: click the <strong className="text-white">install icon</strong> in the address bar</p>
                            <p className="flex items-center gap-1.5"><Download className="w-3.5 h-3.5 inline flex-shrink-0" /> In Safari: go to <strong className="text-white">File &gt; Add to Dock</strong></p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
